/**
 * Fines (gas-layer) GLSL. Velocity is an analytic gravity current plus
 * 2-octave Bridson curl noise. Constants are documented next to the terms
 * they scale — keep CPU spawn (`cloudSpawn.ts`) in the same units.
 *
 *   MUSHROOM_DUR     0.65 s   buoyant vortex-ring window
 *   MUSHROOM_CORE_R  0.55 m   radial falloff of the lift
 *   VORTEX_RING_R    0.48 m
 *   VORTEX_RING_H    0.34 m   ring height above the epicenter
 *   VORTEX_STRENGTH  3.1      circulation scale (× energyLaunch)
 *   SLUMP_START      0.45 s
 *   SLUMP_ACCEL      3.4 m/s² dust-laden negative buoyancy
 *   GROUND_BLEND_LO  0.10 m   fully in the current
 *   GROUND_BLEND_HI  0.72 m   fully airborne
 *   GROUND_R0        0.85 m   1/r reference radius (× energyRadius)
 *   GROUND_SPEED     2.55     radial front speed at r0 (× energyLaunch)
 *   FRONT_ROLL       0.55     upward kick at the lobed head
 *   LOBE_N           5        azimuthal lobes
 *   LOBE_AMP         0.26
 *   CURL_AMP         0.92
 *   CURL_SCALE       0.52     noise domain (1/m)
 *   SDF_RAMP         0.40 m   potential → 0 near colliders / surface
 *   DRAG             1.35
 *   WALL_BUOY_SCALE  0.10     mushroom / vortex scale at upFactor=0
 *   CURTAIN_DIST     0.50 m   slide term near a steep SDF face
 *   CURTAIN_SPEED    0.85     slow-particle gate for the curtain
 *   CURTAIN_ACCEL    5.2 m/s² gravity projected onto the wall plane
 */
import { ENERGY_GLSL } from '../../services/energy';
import { COLLIDER_GLSL } from './shaders';
import { GRADE_GLSL } from './gradeGLSL';

export const CLOUD_CONST_GLSL = /* glsl */ `
const float MUSHROOM_DUR = 0.65;
const float MUSHROOM_CORE_R = 0.55;
const float VORTEX_RING_R = 0.48;
const float VORTEX_RING_H = 0.34;
const float VORTEX_STRENGTH = 3.1;
const float SLUMP_START = 0.45;
const float SLUMP_ACCEL = 3.4;
const float GROUND_BLEND_LO = 0.10;
const float GROUND_BLEND_HI = 0.72;
const float GROUND_R0 = 0.85;
const float GROUND_SPEED = 2.55;
const float FRONT_ROLL = 0.55;
const float LOBE_N = 5.0;
const float LOBE_AMP = 0.26;
const float CURL_AMP = 0.92;
const float CURL_SCALE = 0.52;
const float SDF_RAMP = 0.40;
const float CLOUD_DRAG = 1.35;
const float SOFT_PUSH = 0.06;
const float WALL_BUOY_SCALE = 0.10;
const float CURTAIN_DIST = 0.50;
const float CURTAIN_SPEED = 0.85;
const float CURTAIN_ACCEL = 5.2;
`;

export const CURL_NOISE_GLSL = /* glsl */ `
float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.yzx + 33.33);
  return fract((p.x + p.y) * p.z);
}

float valueNoise(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float n000 = hash13(i);
  float n100 = hash13(i + vec3(1.0, 0.0, 0.0));
  float n010 = hash13(i + vec3(0.0, 1.0, 0.0));
  float n110 = hash13(i + vec3(1.0, 1.0, 0.0));
  float n001 = hash13(i + vec3(0.0, 0.0, 1.0));
  float n101 = hash13(i + vec3(1.0, 0.0, 1.0));
  float n011 = hash13(i + vec3(0.0, 1.0, 1.0));
  float n111 = hash13(i + vec3(1.0, 1.0, 1.0));
  float nx00 = mix(n000, n100, u.x);
  float nx10 = mix(n010, n110, u.x);
  float nx01 = mix(n001, n101, u.x);
  float nx11 = mix(n011, n111, u.x);
  return mix(mix(nx00, nx10, u.y), mix(nx01, nx11, u.y), u.z);
}

float potentialRamp(vec3 p) {
  float sdf = sampleColliderSDF(p);
  float hf = sampleHeightfield(p.xz);
  float surf = p.y - hf;
  float d = min(sdf, surf);
  return smoothstep(0.0, SDF_RAMP, d);
}

vec3 vectorPotential(vec3 p, float seed) {
  vec3 q = p * CURL_SCALE + vec3(seed * 17.3, seed * 9.1, seed * 5.7);
  float a = valueNoise(q);
  float b = valueNoise(q + vec3(17.1, 31.7, 11.3));
  float c = valueNoise(q + vec3(27.2, 5.9, 41.6));
  vec3 q2 = q * 2.17 + vec3(4.2, 8.6, 1.9);
  a += 0.5 * valueNoise(q2);
  b += 0.5 * valueNoise(q2 + vec3(17.1, 31.7, 11.3));
  c += 0.5 * valueNoise(q2 + vec3(27.2, 5.9, 41.6));
  return vec3(a, b, c) * potentialRamp(p);
}

vec3 curlNoise(vec3 p, float seed) {
  float e = 0.14;
  vec3 dx = vec3(e, 0.0, 0.0);
  vec3 dy = vec3(0.0, e, 0.0);
  vec3 dz = vec3(0.0, 0.0, e);
  vec3 px1 = vectorPotential(p + dx, seed);
  vec3 px0 = vectorPotential(p - dx, seed);
  vec3 py1 = vectorPotential(p + dy, seed);
  vec3 py0 = vectorPotential(p - dy, seed);
  vec3 pz1 = vectorPotential(p + dz, seed);
  vec3 pz0 = vectorPotential(p - dz, seed);
  float inv = 1.0 / (2.0 * e);
  return vec3(
    (pz1.y - pz0.y - (py1.z - py0.z)) * inv,
    (px1.z - px0.z - (pz1.x - pz0.x)) * inv,
    (py1.x - py0.x - (px1.y - px0.y)) * inv
  );
}
`;

export const CLOUD_INTEGRATE_GLSL = /* glsl */ `
void pushOutOfSolid(inout vec3 pos, inout vec3 vel) {
  float boxD = sampleColliderSDF(pos);
  if (boxD < SOFT_PUSH) {
    vec3 n = colliderGradient(pos);
    pos += n * (SOFT_PUSH - boxD);
    float vn = dot(vel, n);
    if (vn < 0.0) vel -= n * vn;
  }
  float hf = sampleHeightfield(pos.xz);
  float floorY = hf + 0.035;
  if (pos.y < floorY) {
    pos.y = floorY;
    if (vel.y < 0.0) vel.y *= -0.12;
  }
}

void integrateCloud(
  inout vec3 pos,
  inout vec3 vel,
  vec3 origin,
  float energy,
  float seed,
  float buoyancy,
  float age,
  float linger,
  float dt,
  float upFactor,
  float kind
) {
  float upF = clamp(upFactor, 0.0, 1.0);
  float buoyScale = mix(WALL_BUOY_SCALE, 1.0, upF);
  // Brick crater/mortar (kind 0/1) and haze (kind 0/1 + long linger) are
  // coarse grit: stronger slump. Bleed streamers (kind 5) hug the face.
  float settleScale = kind < 1.5 ? 1.70 : 1.0;
  vec3 floorE = vec3(origin.x, sampleHeightfield(origin.xz), origin.z);
  vec3 epicenter = mix(floorE, origin, upF);
  vec3 rel = pos - epicenter;
  float rho = length(rel.xz);
  vec3 eRho = rho > 1e-4 ? vec3(rel.x / rho, 0.0, rel.z / rho) : vec3(1.0, 0.0, 0.0);
  float hf = sampleHeightfield(pos.xz);
  float height = pos.y - hf;
  float groundW = 1.0 - smoothstep(GROUND_BLEND_LO, GROUND_BLEND_HI, height);
  float e = energyLaunch(energy);
  float rScale = energyRadius(energy);
  float vmax = 8.0 + 3.5 * e;

  // 1. Mushroom: buoyant rise + vortex-ring curl near the core, first ~0.65s.
  //    Scaled by upFactor so a vertical wall is a faint warm drift at most.
  float mush = 1.0 - smoothstep(0.42, MUSHROOM_DUR, age);
  float core = exp(-(rho * rho) / (MUSHROOM_CORE_R * MUSHROOM_CORE_R));
  vel.y += buoyancy * core * mush * buoyScale * dt;

  vec3 torus = origin + vec3(0.0, VORTEX_RING_H, 0.0) + eRho * VORTEX_RING_R;
  vec3 toCore = pos - torus;
  vec3 eTheta = normalize(cross(vec3(0.0, 1.0, 0.0), eRho));
  float r2 = dot(toCore, toCore) + 0.045;
  vec3 vVortex = cross(eTheta, toCore) * (VORTEX_STRENGTH * e / r2);
  vel += vVortex * mush * core * buoyScale * dt;

  // 2. Slump: dust-laden air is heavier than ambient after the rise.
  float slump = smoothstep(SLUMP_START, 0.95, age);
  vel.y -= SLUMP_ACCEL * settleScale * slump * (1.0 - groundW * 0.65) * dt;

  // 3. Ground current: radial 1/r front + lobed head with a slight upward roll.
  //    Epicenter is the impact for floors and the floor projection (wall base)
  //    for walls. The SDF ramp already kills backflow into the wall.
  float az = atan(rel.z, rel.x);
  float lobe = 1.0 + LOBE_AMP * sin(az * LOBE_N + seed * 6.2831853);
  float r0 = GROUND_R0 * rScale;
  float vRad = (GROUND_SPEED * e) * (r0 / max(rho, r0)) * lobe;
  vec3 vGround = eRho * vRad;
  float front = smoothstep(r0 * 0.55, r0 * 1.7, rho)
    * (1.0 - smoothstep(r0 * 3.2, r0 * 5.2, rho));
  vGround.y += FRONT_ROLL * front * e;
  vel = mix(vel, vGround + vec3(0.0, vel.y * 0.25, 0.0), groundW * slump);

  // 3b. Wall curtain: slow fines next to a steep face slide down it.
  float sdf = sampleColliderSDF(pos);
  vec3 sn = colliderGradient(pos);
  float steep = 1.0 - smoothstep(0.35, 0.72, abs(sn.y));
  float nearWall = 1.0 - smoothstep(0.08, CURTAIN_DIST, sdf);
  float spd = length(vel);
  float slow = 1.0 - smoothstep(CURTAIN_SPEED * 0.55, CURTAIN_SPEED * 1.35, spd);
  float curtain = steep * nearWall * slow;
  float streamer = step(4.5, kind) * (1.0 - step(5.5, kind));
  curtain = max(curtain, streamer * steep * nearWall);
  vec3 gDown = vec3(0.0, -1.0, 0.0);
  vec3 slide = gDown - sn * dot(gDown, sn);
  vel += slide * CURTAIN_ACCEL * curtain * (1.0 + 0.45 * streamer) * dt;
  if (streamer > 0.5) {
    vel -= sn * max(dot(vel, sn), 0.0) * 0.85;
    vel *= 0.97;
  }

  // 4. Bridson curl — potential ramps to 0 near SDF / surface so flow goes around.
  vel += curlNoise(pos, seed) * CURL_AMP * dt;

  // 5. Drag + life-driven damping. Seed only (no wall-clock).
  float speed = length(vel);
  if (speed > vmax) vel *= vmax / max(speed, 1.0e-6);
  speed = min(speed, vmax);
  float damp = 1.0 / (1.0 + CLOUD_DRAG * speed * dt);
  vel *= damp;

  pos += vel * dt;
  pushOutOfSolid(pos, vel);

  vec2 mn = uArenaMin + vec2(0.08);
  vec2 mx = uArenaMin + uArenaSize - vec2(0.08);
  if (pos.x < mn.x) { pos.x = mn.x; vel.x = abs(vel.x) * 0.25; }
  if (pos.x > mx.x) { pos.x = mx.x; vel.x = -abs(vel.x) * 0.25; }
  if (pos.z < mn.y) { pos.z = mn.y; vel.z = abs(vel.z) * 0.25; }
  if (pos.z > mx.y) { pos.z = mx.y; vel.z = -abs(vel.z) * 0.25; }
}

bool cloudShouldDie(float age, float linger, float height, float slump, vec3 pos, vec3 vel) {
  if (age >= linger) return true;
  float life = 1.0 - age / max(linger, 1.0e-3);
  if (life < 0.03) return true;
  // Dump mass along the front once the slump has put us on the ground.
  if (slump > 0.55 && height < 0.18 && life < 0.22) return true;
  // Curtain sheets that have slid to the wall base deposit there.
  float sdf = sampleColliderSDF(pos);
  vec3 sn = colliderGradient(pos);
  float spd = length(vel);
  if (age > 0.70 && height < 0.14 && sdf < 0.55 && abs(sn.y) < 0.55 && spd < 0.50) {
    return true;
  }
  return false;
}
`;

export function cloudPosFragSource(): string {
  return /* glsl */ `
uniform sampler2D tPos;
uniform sampler2D tVel;
uniform sampler2D tMeta;
uniform sampler2D tOrigin;
uniform sampler2D tColor;
uniform sampler2D tFrame;
uniform float uDt;
varying vec2 vUv;
${COLLIDER_GLSL}
${CLOUD_CONST_GLSL}
${ENERGY_GLSL}
${CURL_NOISE_GLSL}
${CLOUD_INTEGRATE_GLSL}
void main() {
  vec4 posAge = texture2D(tPos, vUv);
  vec4 velLife = texture2D(tVel, vUv);
  vec4 meta = texture2D(tMeta, vUv);
  vec4 originE = texture2D(tOrigin, vUv);
  vec4 col = texture2D(tColor, vUv);
  vec4 frame = texture2D(tFrame, vUv);
  if (posAge.w < -1.5 && posAge.w > -2.5) {
    gl_FragColor = vec4(0.0, -100.0, 0.0, -1.0);
    return;
  }
  if (posAge.w < 0.0) {
    gl_FragColor = posAge;
    return;
  }
  vec3 pos = posAge.xyz;
  vec3 vel = velLife.xyz;
  float linger = max(velLife.w, 0.2);
  float age = posAge.w;
  float hf = sampleHeightfield(pos.xz);
  float height = pos.y - hf;
  float slump = smoothstep(SLUMP_START, 0.95, age);
  float drain = 1.0 + 0.85 * (1.0 - smoothstep(0.10, 0.72, height)) * slump;
  integrateCloud(pos, vel, originE.xyz, originE.w, meta.y, col.a, age, linger, uDt, frame.w, meta.z);
  age += uDt * drain;
  if (cloudShouldDie(age, linger, height, slump, pos, vel)) {
    gl_FragColor = vec4(pos, -2.0);
    return;
  }
  gl_FragColor = vec4(pos, age);
}
`;
}

export function cloudVelFragSource(): string {
  return /* glsl */ `
uniform sampler2D tPos;
uniform sampler2D tVel;
uniform sampler2D tMeta;
uniform sampler2D tOrigin;
uniform sampler2D tColor;
uniform sampler2D tFrame;
uniform float uDt;
varying vec2 vUv;
${COLLIDER_GLSL}
${CLOUD_CONST_GLSL}
${ENERGY_GLSL}
${CURL_NOISE_GLSL}
${CLOUD_INTEGRATE_GLSL}
void main() {
  vec4 posAge = texture2D(tPos, vUv);
  vec4 velLife = texture2D(tVel, vUv);
  vec4 meta = texture2D(tMeta, vUv);
  vec4 originE = texture2D(tOrigin, vUv);
  vec4 col = texture2D(tColor, vUv);
  vec4 frame = texture2D(tFrame, vUv);
  if (posAge.w < -1.5 && posAge.w > -2.5) {
    gl_FragColor = vec4(0.0);
    return;
  }
  if (posAge.w < 0.0) {
    gl_FragColor = velLife;
    return;
  }
  vec3 pos = posAge.xyz;
  vec3 vel = velLife.xyz;
  float linger = max(velLife.w, 0.2);
  float age = posAge.w;
  float hf = sampleHeightfield(pos.xz);
  float height = pos.y - hf;
  float slump = smoothstep(SLUMP_START, 0.95, age);
  float drain = 1.0 + 0.85 * (1.0 - smoothstep(0.10, 0.72, height)) * slump;
  integrateCloud(pos, vel, originE.xyz, originE.w, meta.y, col.a, age, linger, uDt, frame.w, meta.z);
  age += uDt * drain;
  if (cloudShouldDie(age, linger, height, slump, pos, vel)) {
    gl_FragColor = vec4(0.0);
    return;
  }
  gl_FragColor = vec4(vel, linger);
}
`;
}

export const CLOUD_RENDER_VERT = /* glsl */ `
attribute float aId;
uniform sampler2D tPos;
uniform sampler2D tVel;
uniform sampler2D tMeta;
uniform sampler2D tColor;
uniform float uTexSize;
varying vec3 vColor;
varying vec2 vLocal;
varying float vAlpha;
varying vec3 vWorld;
varying float vSize;
varying float vLife;
varying float vVariant;
varying vec2 vMirror;

void main() {
  float id = aId;
  vec2 tuv = (vec2(mod(id, uTexSize), floor(id / uTexSize)) + 0.5) / uTexSize;
  vec4 posAge = texture2D(tPos, tuv);
  vec4 velLife = texture2D(tVel, tuv);
  vec4 meta = texture2D(tMeta, tuv);
  vec4 col = texture2D(tColor, tuv);
  vLocal = uv - 0.5;
  vLife = 0.0;
  vVariant = 0.0;
  vMirror = vec2(0.0);
  if (posAge.w < 0.0) {
    vAlpha = 0.0;
    vColor = vec3(0.0);
    vWorld = vec3(0.0);
    vSize = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  float linger = max(velLife.w, 0.2);
  float t = clamp(posAge.w / linger, 0.0, 1.0);
  float kind = meta.z;
  float streamer = step(4.5, kind) * (1.0 - step(5.5, kind));
  float puff = step(3.5, kind) * (1.0 - step(4.5, kind));
  float haze = step(4.5, linger);
  float fadePow = 1.12;
  if (kind < 1.5 && haze < 0.5) fadePow = 2.45;
  if (haze > 0.5) fadePow = 1.05;
  if (streamer > 0.5) fadePow = 1.35;
  if (puff > 0.5) fadePow = 1.55;
  float growAmt = mix(1.25, 0.18, streamer);
  growAmt = mix(growAmt, 0.55, puff);
  float grow = 1.0 + growAmt * t;
  float size = max(meta.x, 0.08) * grow;
  if (streamer > 0.5) size *= 0.72;
  float fade = pow(1.0 - t, fadePow);
  float thin = 1.0 / (1.0 + 0.85 * t);
  if (haze > 0.5) thin *= 0.42;
  // Peak kept low so stacked premul layers stay smoke, not a poster solid.
  float alphaPeak = mix(0.34, 0.20, streamer);
  alphaPeak = mix(alphaPeak, 0.16, haze);
  float alpha = fade * thin * alphaPeak;
  if (alpha < 0.018) {
    vAlpha = 0.0;
    vColor = vec3(0.0);
    vWorld = vec3(0.0);
    vSize = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vec4 viewCenter = viewMatrix * vec4(posAge.xyz, 1.0);
  vec2 scale = streamer > 0.5 ? vec2(size * 0.42, size * 1.9) : vec2(size, size);
  viewCenter.xy += position.xy * scale * 2.0;
  vColor = col.rgb;
  vAlpha = alpha;
  vWorld = posAge.xyz;
  vSize = size;
  // Flipbook frame from AGE only — monotonic, no hash, no wall-clock.
  vLife = t;
  // Variant + UV mirror from the per-sprite seed (meta.y). Deterministic.
  float seed = meta.y;
  vVariant = step(0.5, seed);
  vMirror = vec2(step(0.5, fract(seed * 17.13)), step(0.5, fract(seed * 31.71)));
  gl_Position = projectionMatrix * viewCenter;
}
`;

export const CLOUD_RENDER_FRAG = /* glsl */ `
uniform vec3 uLightDir;
uniform vec3 uLightColor;
uniform vec3 uHemiSky;
uniform vec3 uHemiGround;
uniform mat4 uCameraMatrixWorld;
uniform sampler2D tHeight;
uniform sampler2D tBase;
uniform sampler2D tSixA;
uniform sampler2D tSixB;
uniform sampler2D tSixA2;
uniform sampler2D tSixB2;
uniform float uSixWay;
uniform vec2 uArenaMin;
uniform vec2 uArenaSize;
uniform vec3 uBoxCenter[16];
uniform vec3 uBoxHalf[16];
uniform int uBoxCount;
varying vec3 vColor;
varying vec2 vLocal;
varying float vAlpha;
varying vec3 vWorld;
varying float vSize;
varying float vLife;
varying float vVariant;
varying vec2 vMirror;
${GRADE_GLSL}

float sdBox(vec3 p, vec3 c, vec3 h) {
  vec3 q = abs(p - c) - h;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

float sampleColliderSDF(vec3 p) {
  float d = p.y;
  for (int i = 0; i < 16; i++) {
    if (i >= uBoxCount) break;
    d = min(d, sdBox(p, uBoxCenter[i], uBoxHalf[i]));
  }
  return d;
}

float sampleHeightfield(vec2 xz) {
  vec2 uv = (xz - uArenaMin) / uArenaSize;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 0.0;
  vec4 hf = texture2D(tHeight, uv);
  return hf.r + hf.a + texture2D(tBase, uv).r;
}

vec2 flipUv(vec2 local01, float frame) {
  float col = mod(frame, 8.0);
  float row = floor(frame / 8.0);
  vec2 inset = local01 * 0.96 + 0.02;
  // flipY=false: PNG top row (frame 0) is uv.y = 0.
  return vec2((col + inset.x) / 8.0, (row + inset.y) / 8.0);
}

vec4 sampleSixAtlas(sampler2D atlas, vec2 local01, float life) {
  // Skip only the first few inject frames. Mid/late tiles carry the tearing.
  float frameF = mix(4.0, 63.0, clamp(life, 0.0, 1.0));
  float f0 = floor(frameF);
  float f1 = min(f0 + 1.0, 63.0);
  float fr = frameF - f0;
  return mix(texture2D(atlas, flipUv(local01, f0)), texture2D(atlas, flipUv(local01, f1)), fr);
}

// Shared silhouette. Atlas density modulates, it does not cut a hard disc.
float softSpriteAlpha(float r2, float vA, float soft, float dens) {
  float disc = exp(-r2 * 2.7);
  float a = vA * disc * soft * mix(0.42, 1.0, clamp(dens, 0.0, 1.0));
  // Stacking cap: many overlaps approach deep smoke, never opaque paint.
  a = a / (1.0 + 1.7 * a);
  return min(a, 0.24);
}

vec3 densityResponse(vec3 lit, float atlasA, float size, float life) {
  float sizeTerm = smoothstep(0.12, 0.50, size);
  float localD = clamp(atlasA, 0.0, 1.0) * mix(0.52, 1.0, sizeTerm) * (1.0 - 0.20 * life);
  float core = smoothstep(0.20, 0.82, localD);
  // Thin haze warms (dust scatter); dense core cools and darkens.
  vec3 warmHaze = vec3(1.06, 0.98, 0.88);
  vec3 coolCore = vec3(0.68, 0.72, 0.80);
  vec3 tint = mix(warmHaze, coolCore, core);
  float darken = mix(1.0, 0.38, core);
  return lit * tint * darken;
}

void main() {
  if (vAlpha < 0.01) discard;
  vec2 q = vLocal * 2.0;
  float r2 = dot(q, q);
  if (r2 > 1.0) discard;

  vec3 camRight = vec3(uCameraMatrixWorld[0][0], uCameraMatrixWorld[0][1], uCameraMatrixWorld[0][2]);
  vec3 camUp = vec3(uCameraMatrixWorld[1][0], uCameraMatrixWorld[1][1], uCameraMatrixWorld[1][2]);
  vec3 camFront = vec3(uCameraMatrixWorld[2][0], uCameraMatrixWorld[2][1], uCameraMatrixWorld[2][2]);
  vec3 L = normalize(uLightDir);
  vec3 lit;

  if (uSixWay > 0.5) {
    vec2 local01 = vLocal + 0.5;
    local01.x = mix(local01.x, 1.0 - local01.x, vMirror.x);
    local01.y = mix(local01.y, 1.0 - local01.y, vMirror.y);
    vec4 sixA;
    vec4 sixB;
    if (vVariant < 0.5) {
      sixA = sampleSixAtlas(tSixA, local01, vLife);
      sixB = sampleSixAtlas(tSixB, local01, vLife);
    } else {
      sixA = sampleSixAtlas(tSixA2, local01, vLife);
      sixB = sampleSixAtlas(tSixB2, local01, vLife);
    }
    // Light in the camera-facing sprite basis. Weights from L only.
    vec3 Ls = vec3(dot(L, camRight), dot(L, camUp), dot(L, camFront));
    vec3 Lp = max(Ls, 0.0);
    vec3 Ln = max(-Ls, 0.0);
    float wSum = Lp.x + Lp.y + Lp.z + Ln.x + Ln.y + Ln.z;
    float inv = wSum > 1e-5 ? 1.0 / wSum : 0.0;
    float shade = (
      sixA.r * Lp.x + sixA.g * Ln.x + sixA.b * Lp.y +
      sixB.r * Ln.y + sixB.g * Lp.z + sixB.b * Ln.z
    ) * inv;
    // Atlas values are single-scatter (dim). Lift contrast so backlit rims
    // and frontlit cores read, with a small ambient floor so sprites don't hole.
    shade = pow(clamp(shade * 2.4, 0.0, 1.0), 0.72);
    vec3 ambient = mix(uHemiGround, uHemiSky, 0.32) * 0.44;
    // Untinted wrap keeps the 6-way gradient alive on a multiply.
    lit = vColor * (ambient + uLightColor * shade) + uLightColor * shade * 0.09;
    lit = densityResponse(lit, sixA.a, vSize, vLife);
    vec3 fragWorld = vWorld + camRight * q.x * vSize + camUp * q.y * vSize;
    float dBox = sampleColliderSDF(fragWorld);
    float dGround = fragWorld.y - sampleHeightfield(fragWorld.xz);
    float soft = smoothstep(0.0, 0.22, min(dBox, dGround));
    float a = softSpriteAlpha(r2, vAlpha, soft, sixA.a);
    if (a < 0.016) discard;
    lit = plFog(plGrade(lit), fragWorld);
    vec3 srgb = linearToOutputTexel(vec4(lit, 1.0)).rgb;
    gl_FragColor = vec4(srgb * a, a);
    return;
  }

  // Phase 2 pyramid / hemisphere normals.
  vec3 nView = normalize(vec3(q, sqrt(max(1.0 - r2, 0.0))));
  vec3 nWorld = normalize((uCameraMatrixWorld * vec4(nView, 0.0)).xyz);
  float ndl = max(dot(nWorld, L), 0.0);
  float hemiT = nWorld.y * 0.5 + 0.5;
  vec3 ambient = mix(uHemiGround, uHemiSky, hemiT);
  lit = densityResponse(vColor * (ambient + uLightColor * ndl), 0.55, vSize, vLife);

  vec3 fragWorld = vWorld + camRight * q.x * vSize + camUp * q.y * vSize;
  float dBox = sampleColliderSDF(fragWorld);
  float dGround = fragWorld.y - sampleHeightfield(fragWorld.xz);
  float soft = smoothstep(0.0, 0.22, min(dBox, dGround));
  float a = softSpriteAlpha(r2, vAlpha, soft, 1.0);
  if (a < 0.016) discard;
  lit = plFog(plGrade(lit), fragWorld);
  vec3 srgb = linearToOutputTexel(vec4(lit, 1.0)).rgb;
  gl_FragColor = vec4(srgb * a, a);
}
`;
